/**
 * Canonical MCP scope taxonomy.
 *
 * One source of truth for:
 *   - apps/mcp/src/scope.ts (consent UI labels, preset bundles)
 *   - apps/api/src/routes/oauthGeneric.ts (DCR + authorize + token mint)
 *   - apps/api/src/auth.ts (per-route enforcement when token.kind === 'mcp')
 *   - apps/web/src/routes/OAuthAuthorizePage.tsx (consent UI scope picker)
 *
 * If you add a scope here, every consumer picks it up at compile time.
 * If you add a write scope, also add it to WRITE_SCOPES below so the
 * `readOnly` flag on minted tokens stays accurate.
 */

export const MCP_SCOPES = {
  READ: 'mcp.read',
  COMMENTS: 'mcp.comments',
  TICKETS_WRITE: 'mcp.tickets.write',
  TICKETS_ASSIGN: 'mcp.tickets.assign',
  JIRA_SEND: 'mcp.jira.send',
} as const;

export type McpScope = (typeof MCP_SCOPES)[keyof typeof MCP_SCOPES];

/** Every scope there is: what a client may ask for and a person may grant. */
export const ALL_MCP_SCOPES: readonly McpScope[] = [
  MCP_SCOPES.READ,
  MCP_SCOPES.COMMENTS,
  MCP_SCOPES.TICKETS_WRITE,
  MCP_SCOPES.TICKETS_ASSIGN,
  MCP_SCOPES.JIRA_SEND,
];

/**
 * Scopes a person grants only by asking for them by name and ticking them
 * (TOADO-849): sending to Jira makes issues in another company's system, so
 * it is never part of a default. The `mcp` shorthand, "Full access", a
 * personal token saved without a list, and the token modal's starting
 * selection all leave these out.
 */
export const OPT_IN_MCP_SCOPES: readonly McpScope[] = [MCP_SCOPES.JIRA_SEND];

/** The scopes a default grant gives: every scope but the opt-in ones. */
export const DEFAULT_MCP_SCOPES: readonly McpScope[] = ALL_MCP_SCOPES.filter((scope) => !OPT_IN_MCP_SCOPES.includes(scope));

/**
 * The OAuth shorthand `scope=mcp` expands to the full set. MCP clients that
 * predate per-tool scopes (or that don't know our taxonomy) typically
 * request this; the consent UI lets the user downgrade before approving.
 */
export const LEGACY_MCP_SHORTHAND = 'mcp';

/**
 * Scopes that confer write privileges. Used to derive `scope.readOnly` on
 * minted tokens — a token whose granted scopes don't intersect this set
 * is read-only, so the existing API mutation guard rejects writes even if
 * the per-tool scope check is bypassed.
 */
export const WRITE_MCP_SCOPES: readonly McpScope[] = [
  MCP_SCOPES.COMMENTS,
  MCP_SCOPES.TICKETS_WRITE,
  MCP_SCOPES.TICKETS_ASSIGN,
  MCP_SCOPES.JIRA_SEND,
];

export function isReadOnlyScopeSet(scopes: readonly string[]): boolean {
  return !scopes.some((s) => (WRITE_MCP_SCOPES as readonly string[]).includes(s));
}

/** What each scope lets a client do, in the words the consent screen and the token modal use. */
export const MCP_SCOPE_LABELS: Record<McpScope, { title: string; desc: string }> = {
  'mcp.read': {
    title: 'Read your tickets and captures',
    desc: 'List companies, projects, tickets, comments, annotations, and capture data. No writes.',
  },
  'mcp.comments': {
    title: 'Post comments on tickets',
    desc: 'Add comments, and edit or delete your own. Cannot change other users\' comments.',
  },
  'mcp.tickets.write': {
    title: 'Create, edit and move tickets',
    desc: 'Create tickets; change their title, description and resolved state; move them between columns or projects; archive, restore and delete them.',
  },
  'mcp.tickets.assign': {
    title: 'Change ticket assignees',
    desc: 'Add or remove users from a ticket\'s assignee list.',
  },
  'mcp.jira.send': {
    title: 'Send tickets to Jira',
    desc: 'Create Jira issues from tickets, in the Jira site this workspace is connected to. Off unless you tick it.',
  },
};

/**
 * The MCP scopes of a personal token saved without a list (TOADO-470): what
 * its read-only flag allows, which is how the API has always enforced it. The
 * MCP server was told such a token had no scopes at all and refused every
 * tool, so every token the app's modal made was dead on arrival.
 */
export function mcpScopesWithoutList(readOnly: boolean): McpScope[] {
  return readOnly ? [MCP_SCOPES.READ] : [...DEFAULT_MCP_SCOPES];
}

/**
 * The scopes a personal token is saved with (TOADO-470): the ones asked for
 * that exist, always including read - a token that cannot read can do nothing
 * else either - and none that write when the token is read-only.
 */
export function personalTokenMcpScopes(requested: readonly string[] | undefined, readOnly: boolean): McpScope[] {
  const asked = requested === undefined ? mcpScopesWithoutList(readOnly) : expandRequestedScopes(requested);
  const granted = new Set<McpScope>([MCP_SCOPES.READ, ...asked]);
  if (readOnly) for (const scope of WRITE_MCP_SCOPES) granted.delete(scope);
  return ALL_MCP_SCOPES.filter((scope) => granted.has(scope));
}

/**
 * Expand the OAuth shorthand `mcp` to the full set; drop unknown values
 * (strict OAuth 2.1 parsing). Used at /oauth/register and /oauth/authorize
 * to normalize what a client requested.
 */
export function expandRequestedScopes(requested: readonly string[]): McpScope[] {
  const out = new Set<McpScope>();
  for (const s of requested) {
    if (s === LEGACY_MCP_SHORTHAND) {
      // The default set: an opt-in scope is granted only when asked for by name (TOADO-849).
      for (const full of DEFAULT_MCP_SCOPES) out.add(full);
      continue;
    }
    if ((ALL_MCP_SCOPES as readonly string[]).includes(s)) {
      out.add(s as McpScope);
    }
  }
  return [...out];
}

/**
 * Which scope a particular REST API route needs when called by a token of
 * `kind === 'mcp'`. Used by the API's per-route guard to enforce scopes on
 * direct REST hits, plugging the gap where a caller could bypass the MCP
 * server with their bearer.
 *
 * Path matching is by prefix-after-method. The matcher is conservative:
 * routes that don't appear here and aren't read-only methods reject MCP
 * tokens by default — opt-in via this list keeps surprises out.
 */
export interface McpRoutePermission {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /**
   * Path pattern. Tokens like `:id` are matched as a single non-/ segment.
   * The pattern matches the path AFTER any version prefix (so use
   * `/tickets/:id/comments`, not `/api/v1/tickets/...`).
   */
  pathPattern: string;
  scope: McpScope;
}

export const MCP_ROUTE_PERMISSIONS: McpRoutePermission[] = [
  // ---- Read (mcp.read) ----
  // GET requests are catch-all'd to mcp.read by the matcher when nothing
  // more specific matches; only list the non-GET reads that need it
  // explicitly.

  // ---- Comments (mcp.comments) ----
  { method: 'POST',   pathPattern: '/tickets/:id/comments',           scope: MCP_SCOPES.COMMENTS },
  { method: 'PATCH',  pathPattern: '/tickets/:tid/comments/:cid',     scope: MCP_SCOPES.COMMENTS },
  { method: 'DELETE', pathPattern: '/tickets/:tid/comments/:cid',     scope: MCP_SCOPES.COMMENTS },

  // ---- Ticket writes (mcp.tickets.write) ----
  { method: 'POST',   pathPattern: '/projects/:projectId/tickets',    scope: MCP_SCOPES.TICKETS_WRITE },
  { method: 'PATCH',  pathPattern: '/tickets/:id',                    scope: MCP_SCOPES.TICKETS_WRITE },
  { method: 'POST',   pathPattern: '/tickets/:id/move',               scope: MCP_SCOPES.TICKETS_WRITE },
  // A fix recorded (TOADO-844): a ticket write, with its comment.
  { method: 'POST',   pathPattern: '/tickets/:id/fixes',              scope: MCP_SCOPES.TICKETS_WRITE },
  // A comment and, by the tool, a move: the route also checks mcp.comments (TOADO-846).
  { method: 'POST',   pathPattern: '/tickets/:id/request-info',       scope: MCP_SCOPES.TICKETS_WRITE },
  // Links and comments on both; the tool then archives (TOADO-847). The route also checks mcp.comments.
  { method: 'POST',   pathPattern: '/tickets/:id/duplicate-of',       scope: MCP_SCOPES.TICKETS_WRITE },
  // Jira (TOADO-849): sending makes issues in Jira, its own opt-in scope; forgetting a link changes only Toado.
  { method: 'POST',   pathPattern: '/tickets/:ticketId/jira-export',  scope: MCP_SCOPES.JIRA_SEND },
  { method: 'POST',   pathPattern: '/projects/:projectId/jira-export', scope: MCP_SCOPES.JIRA_SEND },
  { method: 'DELETE', pathPattern: '/tickets/:ticketId/jira-export',  scope: MCP_SCOPES.TICKETS_WRITE },
  { method: 'POST',   pathPattern: '/tickets/:id/archive',            scope: MCP_SCOPES.TICKETS_WRITE },
  { method: 'POST',   pathPattern: '/tickets/:id/restore',            scope: MCP_SCOPES.TICKETS_WRITE },
  { method: 'DELETE', pathPattern: '/tickets/:id',                    scope: MCP_SCOPES.TICKETS_WRITE },

  // ---- Assignments (mcp.tickets.assign) ----
  { method: 'POST',   pathPattern: '/tickets/:id/assign',             scope: MCP_SCOPES.TICKETS_ASSIGN },
  { method: 'POST',   pathPattern: '/tickets/:id/unassign',           scope: MCP_SCOPES.TICKETS_ASSIGN },
];

/**
 * Match a request method+path against the route table. Returns the
 * required scope, or null when the route shouldn't be reachable by an
 * MCP token at all.
 *
 * GET / HEAD / OPTIONS automatically require `mcp.read` (no need to list
 * every read endpoint individually). The catch-all *won't* fire for
 * non-GET methods — those require an explicit entry, so accidentally
 * unguarded mutation routes default to rejection rather than allow-by-
 * default. That's the safe failure mode.
 */
export function lookupRoutePermission(method: string, path: string): McpScope | null {
  const upperMethod = method.toUpperCase();
  // Strip any `/api` or `/api/v1` prefix — patterns are written as the
  // route relative paths.
  const stripped = path.replace(/^\/api(?:\/v\d+)?/, '');

  // Specific matches win over the catch-all.
  for (const rule of MCP_ROUTE_PERMISSIONS) {
    if (rule.method !== upperMethod) continue;
    if (matchPathPattern(rule.pathPattern, stripped)) return rule.scope;
  }

  if (upperMethod === 'GET' || upperMethod === 'HEAD' || upperMethod === 'OPTIONS') {
    return MCP_SCOPES.READ;
  }

  // Mutation route with no explicit entry — reject MCP tokens by default.
  return null;
}

function matchPathPattern(pattern: string, path: string): boolean {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('?')[0]!.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return false;
  for (let i = 0; i < patternParts.length; i++) {
    const p = patternParts[i]!;
    if (p.startsWith(':')) continue; // any single segment matches
    if (p !== pathParts[i]) return false;
  }
  return true;
}
